import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'underscore';
import {
  EditorState,
  TemplateAction,
  useVideoTemplateEditorState,
  VideoTemplateEditorFeatures,
  VideoTemplateState,
} from 'components/VideoTemplateEditor';
import {
  useIntroOutroUploadIntegration,
  useMediaUploadIntegration,
  useStaticTextIntegration,
} from 'components/VideoTemplateEditor/integrations';
import {
  createStateFromConfig,
  overrideStateContent,
} from 'components/VideoTemplateEditor/state';
import {
  StaticTextIntegration,
  UploadEdgeVideosIntegration,
  UploadMediaIntegration,
} from 'components/VideoTemplateEditor/types/integrations';
import useOnStyleChange from 'components/VideoTemplateEditor/useOnStyleChange';
import { LOCKSPEC_LOCK_MESSAGE } from 'constants/locks';
import { TranscriptionFormValue } from 'containers/TranscriptionForm';
import useCustomFontsLoader from 'hooks/useCustomFontsLoader';
import { ImageProcessorStatus } from 'hooks/useImageProcessor';
import useLastUsedStyleDispatch from 'hooks/useLastUsedStyleDispatch';
import useLastUsedStyleSelector from 'hooks/useLastUsedStyleSelector';
import { hideModal } from 'redux/modules/modal';
import { showError } from 'redux/modules/notification';
import {
  createConfigurationFromScratch,
  formatSoundwave,
  formatTextOverlayInfo,
} from 'redux/modules/wizard-export/actions';
import { captionSourceEddyProjectCuidSelector } from 'redux/modules/wizard-export/selectors';
import { Dispatch } from 'redux/types';
import { IEmbedConfig, TemplateType } from 'types';
import useEditTemplateModalLockSpec from './useEditTemplateModalLockSpec';

export type Integrations = (
  | UploadMediaIntegration
  | StaticTextIntegration
  | UploadEdgeVideosIntegration
)[];

export interface UseEditTemplateModalEditorConfig {
  embedConfig: IEmbedConfig;
  features: VideoTemplateEditorFeatures;
  isShortFormProject: boolean;
}

export interface UseEditTemplateModalEditorResult {
  editorState: VideoTemplateState;
  integrations: Integrations;
  templateEditorFeatures: VideoTemplateEditorFeatures;
  transcription: TranscriptionFormValue;
  imageProcessorStatus: ImageProcessorStatus;
  onSave: () => void;
  onChange: (
    stateUpdater: (prevState: VideoTemplateState) => VideoTemplateState,
    action: TemplateAction,
  ) => void;
  onCancel: () => void;
}

export default function useEditTemplateModalEditor({
  embedConfig,
  features,
  isShortFormProject,
}: UseEditTemplateModalEditorConfig): UseEditTemplateModalEditorResult {
  const { setLastUsedStyle } = useLastUsedStyleDispatch('editTemplateModal');
  const { lastUsedStyle } = useLastUsedStyleSelector('editTemplateModal');
  const captionSourceEddyProjectCuid = useSelector(
    captionSourceEddyProjectCuidSelector,
  );
  // Preloads the fonts for getting the font styles when mounting the overlay.
  useCustomFontsLoader();

  const dispatch = useDispatch<Dispatch>();

  const { audioInfo, captions, dimensions } = embedConfig ?? {};
  const aspectRatio = dimensions?.width / dimensions?.height;

  const { lockSpecUuid, isLockSpecReady } = useEditTemplateModalLockSpec(
    embedConfig,
  );

  const integrations: Integrations = [
    useMediaUploadIntegration({ priority: 1 }),
    useStaticTextIntegration({
      priority: 2,
    }),
    useIntroOutroUploadIntegration({ priority: 3 }),
  ];

  const transcription = useMemo(
    (): TranscriptionFormValue => ({
      transcribe: captions?.enabled,
      language: audioInfo?.[0]?.language,
    }),
    [audioInfo, captions?.enabled],
  );

  const initEditorState = useCallback(
    (): VideoTemplateState =>
      overrideStateContent(createStateFromConfig(embedConfig), {
        aspectRatio,
        isComplex: false,
        template: {
          templateType: TemplateType.USER_GENERATED,
          isUCSEditorCompatible: true,
          isUCSEditorCompatibleLoading: false,
        },
        transcription,
      }),
    [aspectRatio, embedConfig, transcription],
  );

  const [editorState, setEditorState] = useState<VideoTemplateState>(() => {
    if (!embedConfig || !isLockSpecReady) {
      return undefined;
    }

    return initEditorState();
  });

  const {
    features: templateEditorFeatures,
    imageProcessorStatus,
  } = useVideoTemplateEditorState({
    defaults: {
      aspectRatio,
    },
    lockReason: LOCKSPEC_LOCK_MESSAGE,
    lockSpecUuid,
    transcription,
    lastUsedStyle,
    features: {
      templates: 'hidden',
      captions: true,
      progress: isShortFormProject || 'hidden',
      ...features,
    },
    initializeSoundwave: false,
    onError: () =>
      dispatch(
        showError({
          message: 'There was an error editing your template',
          dismissAfterSec: 5,
        }),
      ),
  });

  const handleStyleChange = useOnStyleChange({
    onStyleChange: useCallback(
      style => {
        setLastUsedStyle(omit(style, 'image', 'video'));
      },
      [setLastUsedStyle],
    ),
  });

  const handleChange = useCallback(
    (
      stateUpdater: (prevState: VideoTemplateState) => VideoTemplateState,
      action: TemplateAction,
    ) => {
      setEditorState(prevState => {
        const state = stateUpdater(prevState);
        const content = EditorState.getContent(state);

        handleStyleChange(content, action);

        return state;
      });
    },
    [handleStyleChange],
  );

  const handleSave = useCallback(async (): Promise<void> => {
    if (!editorState) {
      return;
    }

    const exportState = EditorState.exportState(editorState);
    const { soundwave: soundwaveConfig, textOverlays } = exportState;
    const soundwave = soundwaveConfig && formatSoundwave(soundwaveConfig);
    const textOverlayInfo = formatTextOverlayInfo(textOverlays);

    const newEmbedConfig = await dispatch(
      createConfigurationFromScratch(
        {
          ...exportState,
          aspectRatio: dimensions,
          initiateExport: false,
          // Quick Editor state doesn't carry the source template, so the
          // exportState's lockSpecUuid is undefined here; surface the
          // project's own lockSpec uuid so createConfigurationFromScratch
          // re-emits it on the resulting embed config.
          lockSpecUuid: exportState.lockSpecUuid ?? lockSpecUuid,
          transcription: editorState.present.transcription,
        },
        textOverlayInfo,
        soundwave,
      ),
    );

    dispatch(
      hideModal({
        newEmbedConfig,
        captionSourceEddyProjectCuid,
      }),
    );
  }, [
    captionSourceEddyProjectCuid,
    dimensions,
    dispatch,
    editorState,
    lockSpecUuid,
  ]);

  const handleCancel = useCallback((): void => {
    dispatch(hideModal());
  }, [dispatch]);

  useEffect(() => {
    if (!editorState && embedConfig && isLockSpecReady) {
      setEditorState(initEditorState());
    }
  }, [editorState, embedConfig, initEditorState, isLockSpecReady]);

  return {
    editorState,
    integrations,
    templateEditorFeatures,
    transcription,
    imageProcessorStatus,
    onSave: handleSave,
    onChange: handleChange,
    onCancel: handleCancel,
  };
}
